import { useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { resolveQuery } from "@/lib/location";
import { ID_PROFILES } from "@/lib/tick-id";
import { toIsoDate, type BiteEntryInput } from "@/lib/bite-log";

const FIELD =
  "mt-1.5 block min-h-11 w-full rounded-sm border border-input bg-card px-3 py-2.5 text-base text-foreground placeholder:text-muted-foreground/70 [&::-webkit-date-and-time-value]:text-left";
const LABEL = "block text-[0.9375rem] font-semibold text-foreground";
const HINT = "mt-1.5 text-sm leading-relaxed text-muted-foreground";
const OUTLINE_BUTTON =
  "inline-flex min-h-11 cursor-pointer items-center rounded-sm border border-foreground/30 px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-foreground";

/**
 * Record the bite.
 *
 * Only the date is required, because the date is the only field the timeline cannot be
 * built without. Everything else sharpens the answer and nothing else blocks it.
 *
 * "Where were you?" is the question the flow has to ask anyway, so it is also where
 * locality lands: the answer resolves through the same resolveQuery() the homepage
 * checker uses, and the peak season shown back is that state's own sourced text.
 *
 * Set as a printed form: labelled fields on the page, no surrounding panel, so the
 * inputs (a shade lighter than the paper) are what stands out.
 */
export function BiteRecord({
  onSave,
  onCancel,
}: {
  onSave: (input: BiteEntryInput, photo?: File) => void;
  onCancel?: () => void;
}) {
  const today = toIsoDate(new Date());
  const [dateFound, setDateFound] = useState(today);
  const [bodyLocation, setBodyLocation] = useState("");
  const [exposure, setExposure] = useState("");
  const [speciesSlug, setSpeciesSlug] = useState("");
  const [symptoms, setSymptoms] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const place = useMemo(() => (exposure.trim() ? resolveQuery(exposure) : null), [exposure]);
  const previewUrl = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!dateFound || saving) return;
    setSaving(true);
    onSave(
      {
        dateFound,
        bodyLocation: bodyLocation.trim(),
        exposure: exposure.trim(),
        exposureStateAbbr: place?.stateAbbr,
        speciesSlug: speciesSlug || undefined,
        symptoms: symptoms.trim(),
      },
      photo ?? undefined,
    );
  };

  return (
    <form onSubmit={submit} className="max-w-xl space-y-7">
      <p className="text-[0.9375rem] leading-relaxed text-muted-foreground">
        Only the date is needed. Everything else is optional.
      </p>

      <div>
        <label htmlFor="bite-date" className={LABEL}>
          When did you find it?
        </label>
        <input
          id="bite-date"
          type="date"
          required
          max={today}
          value={dateFound}
          onChange={(e) => setDateFound(e.target.value)}
          aria-describedby="bite-date-hint"
          className={FIELD}
        />
        <p id="bite-date-hint" className={HINT}>
          Every window on the next screen is counted from this date, so it is the one thing worth
          getting right. An estimate is fine.
        </p>
      </div>

      <div>
        <label htmlFor="bite-body" className={LABEL}>
          Where on your body?
        </label>
        <input
          id="bite-body"
          type="text"
          value={bodyLocation}
          onChange={(e) => setBodyLocation(e.target.value)}
          placeholder="Back of the left knee"
          className={FIELD}
        />
      </div>

      <div>
        <label htmlFor="bite-exposure" className={LABEL}>
          Where were you when you picked it up?
        </label>
        <input
          id="bite-exposure"
          type="text"
          value={exposure}
          onChange={(e) => setExposure(e.target.value)}
          placeholder="ZIP code, city, or state"
          autoComplete="postal-code"
          className={FIELD}
        />
        <div aria-live="polite">
          {place?.state && (
            <p className={HINT}>
              <span className="font-semibold text-foreground">{place.label}</span>. Peak season
              there: {place.state.peakSeason}.{" "}
              <Link
                to="/states/$state"
                params={{ state: place.state.slug }}
                className="link font-semibold"
              >
                What we publish for {place.state.name}
              </Link>
            </p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="bite-species" className={LABEL}>
          Do you know which tick it was?
        </label>
        <select
          id="bite-species"
          value={speciesSlug}
          onChange={(e) => setSpeciesSlug(e.target.value)}
          aria-describedby="bite-species-hint"
          className={FIELD}
        >
          <option value="">Not sure</option>
          {ID_PROFILES.map((p) => (
            <option key={p.slug} value={p.slug}>
              {p.commonName}
            </option>
          ))}
        </select>
        <p id="bite-species-hint" className={HINT}>
          Naming the tick shortens the watch list to what that species transmits.{" "}
          <Link to="/tick-identification" className="link font-semibold">
            Work out which one it was
          </Link>
          .
        </p>
      </div>

      <div>
        <span className={LABEL}>A photo of the tick</span>
        <p className={HINT}>
          Kept on this device only, never uploaded. Useful weeks later if you need to show someone.
        </p>
        {previewUrl ? (
          <div className="mt-3 flex items-center gap-4">
            <img
              src={previewUrl}
              alt="The tick you photographed"
              className="h-20 w-20 rounded-sm border border-border object-cover"
            />
            <button
              type="button"
              onClick={() => {
                setPhoto(null);
                if (fileRef.current) fileRef.current.value = "";
              }}
              className="link text-sm font-semibold"
            >
              Remove photo
            </button>
          </div>
        ) : (
          <label
            className={`${OUTLINE_BUTTON} mt-3 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ring`}
          >
            Add a photo
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
            />
          </label>
        )}
      </div>

      <div>
        <label htmlFor="bite-symptoms" className={LABEL}>
          Anything else worth remembering?
        </label>
        <textarea
          id="bite-symptoms"
          rows={3}
          value={symptoms}
          onChange={(e) => setSymptoms(e.target.value)}
          placeholder="How long it might have been attached, whether the area is red…"
          className={FIELD}
        />
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-rule/70 pt-6">
        <button
          type="submit"
          disabled={!dateFound || saving}
          className="inline-flex min-h-11 items-center gap-2 rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-dark disabled:opacity-60"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          Save and see what to watch for
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="link text-sm font-semibold">
            Back to the steps
          </button>
        )}
      </div>
    </form>
  );
}
